import type { Page, SitemapResult, Warning } from '../types';

// 任意の Blob をダウンロード扱いで保存させる
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// CSV エスケープ：カンマ・改行・ダブルクオートが入る値はクオートで囲む
function csvEscape(value: unknown): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

// ページごとの警告 type を集約。warning.url が一致するもののみ拾う
// （集計型の warning は url を持たないので CSV 行には乗らない）
function buildWarningsByUrl(warnings: Warning[]): Map<string, string[]> {
  const m = new Map<string, string[]>();
  for (const w of warnings) {
    if (!w.url) continue;
    const arr = m.get(w.url) ?? [];
    arr.push(w.type);
    m.set(w.url, arr);
  }
  return m;
}

// redirectChain を「URL [status] -> URL [status] -> 最終URL」形式の 1 行文字列にする
function redirectChainToString(page: Page): string {
  if (page.redirectChain.length === 0) return '';
  const hops = page.redirectChain.map((h) => `${h.url} [${h.status}]`);
  // 最終 URL は page.url
  hops.push(page.url);
  return hops.join(' -> ');
}

// 配列を区切り文字で結合。max 件で切って、超過分は「(+他 N 件)」と付ける
// 1 セル内で扱える分量を抑え、Excel/Numbers の上限（32767 文字）に当たらないよう
function joinList(items: readonly string[], sep = ' | ', max = 20): string {
  if (items.length === 0) return '';
  if (items.length <= max) return items.join(sep);
  const head = items.slice(0, max).join(sep);
  return `${head}${sep}(+他 ${items.length - max} 件)`;
}

// JSON-LD の @type を抽出。@type は文字列 or 配列のことがある
function extractJsonLdTypes(blocks: readonly unknown[] | undefined): string[] {
  if (!blocks) return [];
  const types: string[] = [];
  for (const b of blocks) {
    if (typeof b !== 'object' || b === null) continue;
    const t = (b as Record<string, unknown>)['@type'];
    if (typeof t === 'string') types.push(t);
    else if (Array.isArray(t)) {
      for (const x of t) if (typeof x === 'string') types.push(x);
    }
  }
  return types;
}

// pages を CSV 文字列に変換。Excel/Numbers 互換のため UTF-8 BOM 付き
// warnings 引数: 集計型の warning（url 無し）はスキップし、url 付きのものだけ
//   該当ページの warnings 列に展開する
//
// CSV 設計方針：
// - 数値カウントだけでなく「中身そのもの」を入れる（h1 テキスト、画像 alt、JSON-LD @type、OGP、hreflang）
// - 1 セルに複数値を入れるときは ` | ` 区切り。長くなりすぎるものは上位 N 件 +「(+他 X 件)」
// - 列順は「概要 → 個別領域（メタ/見出し/画像/構造化/リンク/パフォーマンス/リダイレクト/警告）」
export function pagesToCsv(pages: Page[], warnings: Warning[] = []): Blob {
  const warningsByUrl = buildWarningsByUrl(warnings);
  const headers = [
    // 識別・ステータス
    'url',
    'status',
    'error',
    // メタ情報（本文系）
    'title',
    'titleLength',
    'metaDescription',
    'metaDescriptionLength',
    'metaRobots',
    'lang',
    'canonical',
    'canonicalCheckStatus',
    'canonicalFinalUrl',
    // 見出し（個数 + 実テキスト）
    'h1Count',
    'h1Texts',
    'h2Count',
    'h2Texts',
    'h3Count',
    'h3Texts',
    'h4Count',
    'h5Count',
    'h6Count',
    // OGP
    'ogTitle',
    'ogDescription',
    'ogImage',
    'ogType',
    // hreflang（lang:url 列挙）
    'hreflangs',
    // 構造化データ（個数 + @type 一覧）
    'structuredDataCount',
    'structuredDataTypes',
    // 画像（個数 + alt 欠の URL 一覧）
    'imageCount',
    'imagesWithoutAlt',
    'imagesWithoutAltSrcs',
    // リンク
    'externalLinkCount',
    'internalLinkCount',
    // パフォーマンス
    'ttfbMs',
    'htmlBytes',
    'jsRendered',
    // リダイレクト
    'redirectCount',
    'redirectChain',
    // 警告
    'warnings',
  ];
  const rows = pages.map((p) => {
    // 画像 alt 欠は「undefined or 空文字」を欠扱いに統一（TableView / PageDetailPanel と整合）
    const imagesMissingAlt = (p.images ?? []).filter(
      (img) => img.alt === undefined || img.alt === '',
    );
    const hreflangs = (p.hreflang ?? []).map((h) => `${h.hreflang}:${h.href}`);
    const jsonLdTypes = extractJsonLdTypes(p.structuredData);
    return [
      // 識別・ステータス
      p.url,
      p.status,
      p.error ?? '',
      // メタ情報
      p.title ?? '',
      p.title ? p.title.length : '',
      p.metaDescription ?? '',
      p.metaDescription ? p.metaDescription.length : '',
      p.metaRobots ?? '',
      p.lang ?? '',
      p.canonical ?? '',
      p.canonicalCheck?.status ?? '',
      p.canonicalCheck?.finalUrl ?? '',
      // 見出し
      p.headings?.h1.length ?? 0,
      joinList(p.headings?.h1 ?? []),
      p.headings?.h2.length ?? 0,
      joinList(p.headings?.h2 ?? []),
      p.headings?.h3.length ?? 0,
      joinList(p.headings?.h3 ?? []),
      p.headings?.h4.length ?? 0,
      p.headings?.h5.length ?? 0,
      p.headings?.h6.length ?? 0,
      // OGP
      p.ogp?.title ?? '',
      p.ogp?.description ?? '',
      p.ogp?.image ?? '',
      p.ogp?.type ?? '',
      // hreflang
      joinList(hreflangs),
      // 構造化データ
      p.structuredData?.length ?? 0,
      joinList(jsonLdTypes),
      // 画像
      p.images?.length ?? 0,
      imagesMissingAlt.length,
      joinList(imagesMissingAlt.map((img) => img.src), ' | ', 10),
      // リンク
      p.externalLinks?.length ?? 0,
      p.internalLinks?.length ?? 0,
      // パフォーマンス
      p.performance?.ttfb ?? '',
      p.performance?.htmlBytes ?? '',
      p.jsRendered ?? '',
      // リダイレクト
      p.redirectChain.length,
      redirectChainToString(p),
      // 警告
      (warningsByUrl.get(p.url) ?? []).join(';'),
    ]
      .map(csvEscape)
      .join(',');
  });
  const csv = [headers.join(','), ...rows].join('\r\n');
  // UTF-8 BOM を付与
  const bom = '﻿';
  return new Blob([bom + csv], { type: 'text/csv;charset=utf-8' });
}

// sitemap.xml を生成。status 200 のページのみ。
// canonical はクロール対象ホスト内の値のときだけ採用し、
// 外部ドメインや別サブドメインを指す canonical は無視して page.url を使う。
// （他社ドメインを自サイトの sitemap.xml に書き込む事故を防ぐ）
export function buildSitemapXml(result: SitemapResult): Blob {
  const seen = new Set<string>();
  const urls: { loc: string; lastmod: string }[] = [];
  const lastmod = result.crawl.completedAt.split('T')[0]!; // YYYY-MM-DD

  let seedHost = '';
  try {
    seedHost = new URL(result.crawl.seedUrl).host;
  } catch {
    /* seedUrl が不正な場合は canonical 検証を行わずフォールバック */
  }

  const isSameHost = (urlStr: string): boolean => {
    if (!seedHost) return true;
    try {
      return new URL(urlStr).host === seedHost;
    } catch {
      return false;
    }
  };

  for (const page of result.pages) {
    if (page.status !== 200) continue;
    const loc =
      page.canonical && isSameHost(page.canonical) ? page.canonical : page.url;
    if (seen.has(loc)) continue;
    seen.add(loc);
    urls.push({ loc, lastmod });
  }

  const xmlEscape = (s: string): string =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  const body = urls
    .map((u) => `  <url>\n    <loc>${xmlEscape(u.loc)}</loc>\n    <lastmod>${u.lastmod}</lastmod>\n  </url>`)
    .join('\n');

  const xml = `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${body}\n</urlset>\n`;
  return new Blob([xml], { type: 'application/xml;charset=utf-8' });
}

// クロール対象ホストを抽出してファイル名に使う
export function defaultFileName(result: SitemapResult, ext: string): string {
  let host = 'sitemap';
  try {
    host = new URL(result.crawl.seedUrl).host.replace(/[^a-zA-Z0-9.-]/g, '-');
  } catch {
    /* keep default */
  }
  return `sitemap-${host}.${ext}`;
}
